import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  X, 
  Users, 
  FileText, 
  Receipt,
  Loader2,
  ArrowRight,
  Command
} from 'lucide-react';
import { useDebounce } from '../hooks/useDebounce';
import { searchAPI } from '../services/api';
import { cn } from '../utils/cn';

// Icon mapping for different result types
const typeIcons = {
  customer: Users,
  enquiry: FileText,
  quotation: FileText,
  serviceRequestForm: FileText,
  proformaInvoice: Receipt,
};

// Color mapping for different result types
const typeColors = {
  customer: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',
  enquiry: 'text-purple-500 bg-purple-100 dark:bg-purple-900/30',
  quotation: 'text-orange-500 bg-orange-100 dark:bg-orange-900/30',
  serviceRequestForm: 'text-green-500 bg-green-100 dark:bg-green-900/30',
  proformaInvoice: 'text-emerald-500 bg-emerald-100 dark:bg-emerald-900/30',
};

// Status badge colors
const statusColors = {
  DRAFT: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
  SUBMITTED: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  APPROVED: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  REJECTED: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  PAYMENT_RECEIVED: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
};

// Navigation routes for different result types
const typeRoutes = {
  customer: '/customers',
  enquiry: (id) => `/enquiries/${id}`,
  quotation: (id) => `/quotations/${id}`,
  serviceRequestForm: (id) => `/service-request-forms/${id}`,
  proformaInvoice: (id) => `/proforma-invoices/${id}`,
};

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  
  // Debounce search query with 300ms delay
  const debouncedQuery = useDebounce(searchQuery, 300);

  // Flattened results for keyboard navigation
  const flattenedResults = results ? [
    ...(results.customers || []).map(r => ({ ...r, type: 'customer' })),
    ...(results.enquiries || []).map(r => ({ ...r, type: 'enquiry' })),
    ...(results.quotations || []).map(r => ({ ...r, type: 'quotation' })),
    ...(results.serviceRequestForms || []).map(r => ({ ...r, type: 'serviceRequestForm' })),
    ...(results.proformaInvoices || []).map(r => ({ ...r, type: 'proformaInvoice' }))
  ] : [];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut (Ctrl/Cmd + K) to focus search
  useEffect(() => {
    function handleKeyDown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      
      if (event.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }

      if (isOpen && flattenedResults.length > 0) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setSelectedIndex(prev => (prev < flattenedResults.length - 1 ? prev + 1 : prev));
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
        } else if (event.key === 'Enter' && selectedIndex >= 0) {
          event.preventDefault();
          const item = flattenedResults[selectedIndex];
          handleResultClick(item.type, item.id);
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, flattenedResults, selectedIndex]);

  // Perform search when debounced query changes
  useEffect(() => {
    async function performSearch() {
      if (!debouncedQuery || debouncedQuery.length < 2) {
        setResults(null);
        setSelectedIndex(-1);
        return;
      }

      setIsLoading(true);
      try {
        const response = await searchAPI.globalSearch(debouncedQuery);
        setResults(response.data.data);
        setSelectedIndex(0); // Select first result by default
      } catch (error) {
        console.error('Search error:', error);
        setResults(null);
      } finally {
        setIsLoading(false);
      }
    }

    performSearch();
  }, [debouncedQuery]);

  // Handle result click
  const handleResultClick = useCallback((type, id) => {
    setIsOpen(false);
    setSearchQuery('');
    setSelectedIndex(-1);
    
    const route = typeof typeRoutes[type] === 'function' 
      ? typeRoutes[type](id) 
      : typeRoutes[type];
    
    navigate(route);
  }, [navigate]);

  // Clear search
  const handleClear = () => {
    setSearchQuery('');
    setResults(null);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Check if we have any results
  const hasResults = flattenedResults.length > 0;

  // Render a single search result item
  const renderResultItem = (item, index, globalIndex) => {
    const Icon = typeIcons[item.type] || FileText;
    const colorClass = typeColors[item.type] || typeColors.enquiry;
    const isSelected = selectedIndex === globalIndex;
    
    return (
      <button
        key={`${item.type}-${item.id}-${index}`}
        onClick={() => handleResultClick(item.type, item.id)}
        onMouseEnter={() => setSelectedIndex(globalIndex)}
        role="option"
        aria-selected={isSelected}
        className={cn(
          "w-full flex items-center gap-3 px-4 py-2.5 transition-all text-left group rounded-lg",
          isSelected 
            ? "bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500 pl-3" 
            : "hover:bg-light-sidebar dark:hover:bg-dark-sidebar border-l-4 border-transparent"
        )}
      >
        <div className={cn(
          "w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 transition-transform duration-200",
          colorClass,
          isSelected && "scale-110"
        )}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn(
              "font-semibold text-sm truncate",
              isSelected ? "text-primary-600 dark:text-primary-400" : "text-light-text dark:text-dark-text"
            )}>
              {item.title}
            </span>
            {item.status && (
              <span className={cn(
                "px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider",
                statusColors[item.status] || statusColors.DRAFT
              )}>
                {item.status.replace(/_/g, ' ')}
              </span>
            )}
          </div>
          {item.subtitle && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {item.subtitle}
            </p>
          )}
        </div>
        <ArrowRight className={cn(
          "w-4 h-4 text-primary-500 transition-all duration-200",
          isSelected ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"
        )} />
      </button>
    );
  };

  // Render a category of results
  const renderCategory = (title, items, startIndex) => {
    if (!items || items.length === 0) return { component: null, count: 0 };
    
    return {
      component: (
        <div className="py-2">
          <div className="px-4 py-1.5">
            <span className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.16em]">
              {title}
            </span>
          </div>
          <div className="px-2 space-y-0.5">
            {items.map((item, index) => renderResultItem({ ...item, type: title.toLowerCase().replace(/ /g, ''), type: title === 'Customers' ? 'customer' : title === 'Enquiries' ? 'enquiry' : title === 'Quotations' ? 'quotation' : title === 'Service Request Forms' ? 'serviceRequestForm' : 'proformaInvoice' }, index, startIndex + index))}
          </div>
        </div>
      ),
      count: items.length
    };
  };

  const customersCat = renderCategory('Customers', results?.customers, 0);
  const enquiriesCat = renderCategory('Enquiries', results?.enquiries, customersCat.count);
  const quotationsCat = renderCategory('Quotations', results?.quotations, customersCat.count + enquiriesCat.count);
  const srfCat = renderCategory('Service Request Forms', results?.serviceRequestForms, customersCat.count + enquiriesCat.count + quotationsCat.count);
  const piCat = renderCategory('Proforma Invoices', results?.proformaInvoices, customersCat.count + enquiriesCat.count + quotationsCat.count + srfCat.count);

  return (
    <div ref={searchRef} className="relative flex-1 max-w-xl group">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          <Search className={cn(
            "w-4 h-4 transition-colors",
            isOpen ? "text-primary-500" : "text-gray-400 group-hover:text-gray-500"
          )} />
        </div>
        <input
          ref={inputRef}
          type="text"
          placeholder="Search ERP... (Ctrl+K)"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className={cn(
            "w-full pl-10 pr-12 py-2.5 rounded-xl transition-all duration-200",
            "bg-light-sidebar dark:bg-dark-sidebar",
            "border border-light-border dark:border-dark-border",
            "text-sm text-light-text dark:text-dark-text placeholder-gray-400",
            "focus:bg-light-surface dark:focus:bg-dark-surface focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500/50 focus:outline-none",
            isOpen && "rounded-b-none border-b-transparent"
          )}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls="search-results"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
          {searchQuery ? (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          ) : (
            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-light-border dark:border-dark-border bg-light-surface dark:bg-dark-surface opacity-40">
              <Command className="w-2.5 h-2.5" />
              <span className="text-xs font-bold">K</span>
            </div>
          )}
        </div>
      </div>

      {/* Search Results Dropdown */}
      <AnimatePresence>
        {isOpen && searchQuery.length >= 2 && (
          <motion.div 
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            id="search-results"
            role="listbox"
            className={cn(
              "absolute top-full left-0 right-0 z-50 overflow-hidden",
              "bg-light-surface dark:bg-dark-surface",
              "border border-light-border dark:border-dark-border border-t-0",
              "shadow-2xl rounded-b-2xl max-h-[70vh] overflow-y-auto custom-scrollbar"
            )}
          >
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-3">
                <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
                <p className="text-sm font-medium text-gray-500 animate-pulse">Searching the vault...</p>
              </div>
            ) : hasResults ? (
              <div className="pb-2">
                {customersCat.component}
                {enquiriesCat.component}
                {quotationsCat.component}
                {srfCat.component}
                {piCat.component}
                <div className="px-4 py-2 mt-2 border-t border-light-border dark:border-dark-border flex items-center justify-between">
                  <p className="text-xs text-gray-400 font-medium">
                    {flattenedResults.length} results found
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <span className="text-xs px-1 rounded bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">↑↓</span>
                      <span className="text-xs text-gray-400">Navigate</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs px-1 rounded bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">Enter</span>
                      <span className="text-xs text-gray-400">Select</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-16 text-center px-6">
                <div className="w-16 h-16 bg-light-sidebar dark:bg-dark-sidebar rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                </div>
                <p className="text-base font-bold text-light-text dark:text-dark-text">
                  No results found
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-[200px] mx-auto">
                  We couldn't find anything matching "{searchQuery}"
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
